/**
 * iter75 — Workforce Hub tab registry.
 *
 * Single source of truth used by:
 *   - WorkforceHub.js (renders tabs + chooses which page to mount)
 *   - AdminLayout.js  (passed to SidebarLayout so each tab is a pinnable Favorite)
 *
 * Adding a new tab? Add it here, add the path to React Router in App.js, and that's it.
 *
 * iter160 — Each tab can optionally declare `perms: [...]`. A user is shown the
 * tab if their role is in `allow` OR they have any granted permission in `perms`
 * (admin/super_admin always pass). This lets us safely grant Employees access
 * to office_staff who hold `view_employees` / `manage_employees` without
 * adding them to the role allow-list (which would over-grant elsewhere).
 */
import {
  UserCog, Truck, Clock, CalendarCheck, ClipboardCheck, UserCheck,
  Award, Wallet, Briefcase, Gift,
} from 'lucide-react';

const ADMIN_TIER = new Set(['super_admin', 'admin']);
const MANAGER_TIER = new Set(['service_manager', 'construction_manager', 'office_staff', 'manager']);
const ALL_INTERNAL = new Set([...ADMIN_TIER, ...MANAGER_TIER]);
const REVIEWER_TIER = new Set(['super_admin', 'admin', 'manager', 'service_manager', 'construction_manager']);

export const WORKFORCE_HUB_BASE = '/admin/workforce';

export const WORKFORCE_TABS = [
  { key: 'employees',          label: 'Employees',          icon: UserCog,         path: '/admin/workforce/employees',          allow: new Set([...ADMIN_TIER, 'service_manager', 'construction_manager']),
    // iter160 — Office staff or any role granted ANY employee-management perm sees this tab.
    perms: ['view_employees', 'manage_employees', 'invite_employees', 'onboard_employees', 'edit_employee_profile', 'reset_employee_password', 'deactivate_employee', 'archive_employee', 'view_employee_documents'] },
  { key: 'my-field-schedule',  label: 'My Field Schedule',  icon: Truck,           path: '/admin/workforce/my-field-schedule',  allow: ALL_INTERNAL },
  { key: 'my-timesheet',       label: 'My Timesheet',       icon: Clock,           path: '/admin/workforce/my-timesheet',       allow: ALL_INTERNAL },
  { key: 'timesheets',         label: 'Timesheets',         icon: Clock,           path: '/admin/workforce/timesheets',         allow: ALL_INTERNAL },
  { key: 'time-off',           label: 'Time Off',           icon: CalendarCheck,   path: '/admin/workforce/time-off',           allow: ALL_INTERNAL },
  { key: 'holidays',           label: 'Holidays & Benefits', icon: Gift,           path: '/admin/workforce/holidays',           allow: new Set([...ADMIN_TIER, 'service_manager', 'construction_manager', 'manager', 'office_staff']),
    perms: ['manage_employees', 'edit_pto_balance'] },
  { key: 'job-approvals',      label: 'Job Approvals',      icon: ClipboardCheck,  path: '/admin/workforce/job-approvals',      allow: REVIEWER_TIER },
  { key: 'approvals',          label: 'Approvals & Invites', icon: UserCheck,      path: '/admin/workforce/approvals',          allow: ADMIN_TIER,
    perms: ['invite_employees', 'onboard_employees'] },
  { key: 'vehicles',           label: 'Vehicles',           icon: Briefcase,       path: '/admin/workforce/vehicles',           allow: new Set([...ADMIN_TIER, 'service_manager', 'construction_manager']) },
  { key: 'bonuses',            label: 'Bonuses',            icon: Award,           path: '/admin/workforce/bonuses',            allow: ALL_INTERNAL,
    perms: ['view_bonuses', 'request_bonus', 'approve_bonus_requests', 'create_bonus_for_employee', 'mark_bonus_paid'] },
  { key: 'payroll',            label: 'Payroll & Payouts',  icon: Wallet,          path: '/admin/workforce/payroll',            allow: ADMIN_TIER,
    perms: ['view_payroll', 'edit_payroll'] },
];

// iter75 — Maps each workforce tab key to the legacy URL it replaces so we can render
// <Navigate replace> redirects for backward compatibility (old bookmarks, screenshots, etc).
export const WORKFORCE_LEGACY_REDIRECTS = [
  { from: '/admin/employees',       to: '/admin/workforce/employees' },
  { from: '/admin/my-timesheet',    to: '/admin/workforce/my-timesheet' },
  { from: '/admin/timesheets',      to: '/admin/workforce/timesheets' },
  { from: '/admin/timeoff',         to: '/admin/workforce/time-off' },
  { from: '/admin/job-approvals',   to: '/admin/workforce/job-approvals' },
  { from: '/admin/approvals',       to: '/admin/workforce/approvals' },
  { from: '/admin/vehicles',        to: '/admin/workforce/vehicles' },
  { from: '/admin/bonus-requests',  to: '/admin/workforce/bonuses' },
  { from: '/admin/payroll',         to: '/admin/workforce/payroll' },
];
